import React, {useEffect, useMemo, useRef, useState} from 'react';

type Money = {cents: number; formatted: string};

type GiftBoxOption = {
  title: string;
  handle: string;
  variantId: number;
  price: Money;
};

type VariantOption = {
  id: number;
  title: string;
  options?: string[];
  available: boolean;
  price: Money;
};

type ProductData = {
  id: number;
  title: string;
  handle: string;
  image?: string | null;
  url?: string | null;
  optionNames?: string[];
  variants: VariantOption[];
  giftBoxes: GiftBoxOption[];
  supportsPersonalisation?: boolean;
  monogramVariantId?: number | null;
};

type StepData = {
  id: 1 | 2;
  title: string;
  products: ProductData[];
};

type BuilderStrings = {
  viewDetails: string;
  quickAdd: string;
  added: string;
  select: string;
  chooseOptionsFor: string;
  close: string;
  reviewDescription: string;
  emptyState: string;
  goToStep1: string;
  items: string;
  subtotal: string;
  boxSubtotal: string;
  cartError: string;
  addToCart: string;
  stepperLabel: string;
  loadingProducts: string;
  scrollLeft: string;
  scrollRight: string;
  hideDetails: string;
  showDetails: string;
  nextStep: string;
  reviewSelection: string;
  selectionSummary: string;
  selectedItems: string;
  remove: string;
  noteLabel: string;
  addWithTitle: string;
  giftNotePlaceholder: string;
  emojisNotSupported: string;
  giftBoxDescription: string;
  addPersonalisation: string;
  personalisationAdded: string;
  personalisationInitialsLabel: string;
  personalisationFontLabel: string;
  personalisationColourLabel: string;
  addToWishlist: string;
  giftingOptionsTitle: string;
  addGiftBoxTitle: string;
  addGiftBox: string;
  edit: string;
  confirmChoices: string;
};

type BuilderData = {
  title: string;
  description?: string;
  titleAlignDesktop?: string;
  titleAlignMobile?: string;
  steps: [StepData, StepData];
  step_3_title?: string;
  monogramPriceCents?: number;
  strings?: BuilderStrings;
};

type PersonalisationData = {
  initials: string;
  font: string;
  color: string;
};

type SelectedLine = {
  key: string; // stable internal key
  stepId: 1 | 2;
  productId: number;
  productTitle: string;
  productHandle: string;
  image?: string | null;
  variantId: number;
  variantTitle: string;
  quantity: number;
  unitPrice: Money;
  groupId: string;
  giftBox?: GiftBoxOption | null;
  giftNote?: string | null;
  personalisation?: PersonalisationData | null;
  monogramVariantId?: number | null;
};

const SESSION_STORAGE_KEY = 'bsb:bridal-set-builder:v1';

type StoredLine = {
  stepId: 1 | 2;
  productId: number;
  variantId: number;
  quantity: number;
  groupId: string;
  giftBoxVariantId?: number | null;
  giftNote?: string | null;
  personalisation?: PersonalisationData | null;
  monogramVariantId?: number | null;
};

type StoredStateV1 = {
  v: 1;
  step: 1 | 2 | 3;
  maxStepReached: 1 | 2 | 3;
  selectedOptionsByProductId: Record<number, string[]>;
  lines: StoredLine[];
};

function formatMoneyFallback(cents: number): string {
  const pounds = cents / 100;
  return `£${pounds % 1 === 0 ? pounds.toFixed(0) : pounds.toFixed(2)}`;
}

function moneyHtml(formatted: string | undefined, centsFallback: number): string {
  const html = (formatted || '').trim();
  return html.length ? html : formatMoneyFallback(centsFallback);
}

function sumSubtotalCents(lines: SelectedLine[], monogramPriceCents = 0): number {
  return lines.reduce((acc, l) => {
    let total = l.unitPrice.cents * l.quantity;
    if (l.giftBox) total += l.giftBox.price.cents * l.quantity;
    if (l.personalisation) total += monogramPriceCents * l.quantity;
    return acc + total;
  }, 0);
}

function firstAvailableVariant(p: ProductData): VariantOption | null {
  return p.variants.find(v => v.available) ?? p.variants[0] ?? null;
}

function variantMatchesOptions(variant: VariantOption, selected: Array<string | null | undefined>): boolean {
  const opts = variant.options || [];
  for (let i = 0; i < selected.length; i++) {
    const want = selected[i];
    if (!want) continue;
    if ((opts[i] || '') !== want) return false;
  }
  return true;
}

function chooseVariantForSelection(product: ProductData, selected: Array<string | null | undefined>): VariantOption | null {
  const avail = product.variants.find(v => v.available && variantMatchesOptions(v, selected));
  if (avail) return avail;
  return product.variants.find(v => variantMatchesOptions(v, selected)) ?? null;
}

function uid(): string {
  return `${Date.now()}-${Math.floor(Math.random() * 100000)}`;
}

function safeParseStoredState(raw: string | null): StoredStateV1 | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredStateV1>;
    if (parsed.v !== 1) return null;
    if (![1, 2, 3].includes(parsed.step as any)) return null;
    if (![1, 2, 3].includes(parsed.maxStepReached as any)) return null;
    if (!parsed.lines || !Array.isArray(parsed.lines)) return null;
    if (!parsed.selectedOptionsByProductId || typeof parsed.selectedOptionsByProductId !== 'object') return null;
    return parsed as StoredStateV1;
  } catch {
    return null;
  }
}

function buildProductIndex(data: BuilderData) {
  const products = [...(data.steps?.[0]?.products ?? []), ...(data.steps?.[1]?.products ?? [])];
  const productById = new Map<number, ProductData>();
  const variantById = new Map<number, {product: ProductData; variant: VariantOption}>();

  for (const p of products) {
    productById.set(p.id, p);
    for (const v of p.variants) {
      variantById.set(v.id, {product: p, variant: v});
    }
  }

  return {productById, variantById};
}

function BagIcon({className}: {className?: string}) {
  return (
    <svg className={className} width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M3.88544 3.75H0.75V12.75H12.75V3.75H9.61455H8.9772H4.52279H3.88544ZM9.7341 3H12.75H13.5V3.75V12.75V13.5H12.75H0.75H0V12.75V3.75V3H0.75H3.76587C3.75536 2.90736 3.75 2.81352 3.75 2.71875C3.75 1.12628 5.19037 0 6.75 0C8.30962 0 9.75 1.12628 9.75 2.71875C9.75 2.81352 9.7446 2.90736 9.7341 3ZM8.9772 3C8.9922 2.90814 9 2.81424 9 2.71875C9 2.62326 8.9922 2.52936 8.9772 2.4375C8.96685 2.37398 8.95298 2.31142 8.9358 2.25C8.6955 1.389 7.80802 0.75 6.75 0.75C5.69198 0.75 4.8045 1.389 4.56417 2.25C4.54702 2.31142 4.53317 2.37397 4.52279 2.4375C4.50777 2.52936 4.5 2.62326 4.5 2.71875C4.5 2.81424 4.50777 2.90814 4.52279 3H8.9772Z" fill="#F3F3F3"/>
    </svg>
    
  );
}

async function addToCartAndCheckout(lines: SelectedLine[]) {
  const items: Array<{id: number; quantity: number; properties?: Record<string, any>}> = [];

  for (const line of lines) {
    const baseProps: Record<string, any> = {
      _journey: 'bridal-set-builder',
      _journeyStep: line.stepId === 1 ? 'bride' : 'bridesmaids',
      _groupId: line.groupId,
    };

    if (line.giftNote && line.giftNote.trim()) {
      baseProps.Note = line.giftNote.trim();
    }

    if (line.giftBox && line.giftBox.variantId && line.giftBox.handle) {
      baseProps._hasGiftBox = 'true';
      baseProps._giftPid = String(line.giftBox.variantId);
      baseProps._giftProductHandle = line.giftBox.handle;
    }

    if (line.personalisation) {
      const strings = (window as any).personalisationStrings ?? {};
      const initialsKey = strings.initialsProperty || 'Monogram';
      const fontKey = strings.fontProperty || 'Font';
      const colorKey = strings.colorProperty || 'Colour';
      baseProps[initialsKey] = line.personalisation.initials;
      baseProps[fontKey] = line.personalisation.font;
      baseProps[colorKey] = line.personalisation.color;
    }

    items.push({
      id: line.variantId,
      quantity: line.quantity,
      properties: baseProps,
    });

    if (line.giftBox && line.giftBox.variantId && line.giftBox.handle) {
      items.push({
        id: line.giftBox.variantId,
        quantity: line.quantity,
        properties: {
          'Gift Box With': `${line.productTitle}${line.variantTitle ? ` — ${line.variantTitle}` : ''}`,
          _parentPid: String(line.variantId),
          _groupId: line.groupId,
          _addon: true,
          _addonType: 'Gift Box',
          _giftProductHandle: line.giftBox.handle,
        },
      });
    }

    if (line.personalisation && line.monogramVariantId) {
      items.push({
        id: line.monogramVariantId,
        quantity: line.quantity,
        properties: {
          _addon: true,
          _addonType: 'Personalisation',
          _parentPid: String(line.variantId),
          _groupId: line.groupId,
        },
      });
    }
  }

  const sections = 'cart-drawer,cart-icon-bubble';
  const sectionsUrl = `${window.location.pathname}${window.location.search}`;

  const res = await fetch(`${(window as any).Shopify?.routes?.root || '/'}cart/add.js`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
    body: JSON.stringify({items, sections, sections_url: sectionsUrl}),
  });

  const json = await res.json().catch(() => null as any);

  if (!res.ok || !json || (json as any).status) {
    const message =
      (json && (json as any).description) || `Cart add failed: ${res.status}`;
    throw new Error(message);
  }

  // Notify other cart listeners and update header count
  try {
    if (typeof (window as any).publish === 'function' && (window as any).PUB_SUB_EVENTS) {
      (window as any).publish((window as any).PUB_SUB_EVENTS.cartUpdate, {
        source: 'bridal-set-builder',
        cartData: json,
      });
    }
    if (typeof (window as any).updateCartItemCount === 'function') {
      (window as any).updateCartItemCount();
    }
  } catch {
    // ignore pub/sub errors
  }

  // Render or open the cart drawer with fresh sections
  if (typeof document !== 'undefined') {
    const drawer = document.querySelector('cart-drawer') as any | null;
    if (
      drawer &&
      typeof drawer.renderContents === 'function' &&
      json.sections &&
      json.sections['cart-drawer'] &&
      String(json.sections['cart-drawer']).trim().length
    ) {
      try {
        drawer.renderContents(json);
        drawer.classList.remove('is-empty');
      } catch {
        if (typeof drawer.open === 'function') {
          drawer.open();
        }
      }
    } else if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }
}

export function BridalSetBuilderApp({data}: {data: BuilderData}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [maxStepReached, setMaxStepReached] = useState<1 | 2 | 3>(1);
  const [openKitProductId, setOpenKitProductId] = useState<number | null>(null);
  const [selectedOptionsByProductId, setSelectedOptionsByProductId] = useState<Record<number, string[]>>({});
  const [openSelectorIndex, setOpenSelectorIndex] = useState<number | null>(null);

  const [lines, setLines] = useState<SelectedLine[]>([]);
  const stickyListRef = useRef<HTMLDivElement | null>(null);
  const gridLoadedIdsRef = useRef<Set<number>>(new Set());
  const [gridLoadedCount, setGridLoadedCount] = useState(0);
  const [showGridLoader, setShowGridLoader] = useState(true);
  const [activeGiftLine, setActiveGiftLine] = useState<SelectedLine | null>(null);
  const [activeGiftProduct, setActiveGiftProduct] = useState<ProductData | null>(null);
  const [stickyDetailsOpen, setStickyDetailsOpen] = useState(false);

  const currentStep = step === 1 ? data.steps[0] : step === 2 ? data.steps[1] : null;
  const subtotalCents = useMemo(() => sumSubtotalCents(lines, data.monogramPriceCents ?? 0), [lines, data.monogramPriceCents]);
  const isGridReady = step === 3 ? true : !showGridLoader;

  // Restore persisted progress + selections (session-scoped).
  useEffect(() => {
    if (typeof window === 'undefined' || !('sessionStorage' in window)) return;

    const stored = safeParseStoredState(window.sessionStorage.getItem(SESSION_STORAGE_KEY));
    if (!stored) return;

    const {productById, variantById} = buildProductIndex(data);

    const restoredLines: SelectedLine[] = [];
    for (const l of stored.lines) {
      const lookup = variantById.get(l.variantId);
      const product = lookup?.product ?? productById.get(l.productId);
      const variant = lookup?.variant ?? product?.variants.find(v => v.id === l.variantId);
      if (!product || !variant) continue;

      const giftBox =
        l.giftBoxVariantId != null
          ? product.giftBoxes.find(g => g.variantId === l.giftBoxVariantId) ?? null
          : null;

      restoredLines.push({
        key: uid(),
        stepId: l.stepId,
        productId: product.id,
        productTitle: product.title,
        productHandle: product.handle,
        image: product.image ?? null,
        variantId: variant.id,
        variantTitle: variant.title,
        quantity: Math.max(1, Number(l.quantity) || 1),
        unitPrice: variant.price,
        groupId: l.groupId || uid(),
        giftBox,
        giftNote: l.giftNote ?? null,
        personalisation: l.personalisation ?? null,
        monogramVariantId: l.monogramVariantId ?? null,
      });
    }

    setLines(restoredLines);
    setSelectedOptionsByProductId(stored.selectedOptionsByProductId || {});

    const clampedMax: 1 | 2 | 3 = stored.maxStepReached === 3 ? 3 : stored.maxStepReached === 2 ? 2 : 1;
    let clampedStep: 1 | 2 | 3 = stored.step === 3 ? 3 : stored.step === 2 ? 2 : 1;
    if (clampedStep > clampedMax) clampedStep = clampedMax;

    setMaxStepReached(clampedMax);
    setStep(clampedStep);
    setOpenKitProductId(null);
    setOpenSelectorIndex(null);
  }, [data]);

  // Persist progress + selections into sessionStorage as user builds.
  useEffect(() => {
    if (typeof window === 'undefined' || !('sessionStorage' in window)) return;
    try {
      const payload: StoredStateV1 = {
        v: 1,
        step,
        maxStepReached,
        selectedOptionsByProductId,
        lines: lines.map(l => ({
          stepId: l.stepId,
          productId: l.productId,
          variantId: l.variantId,
          quantity: l.quantity,
          groupId: l.groupId,
          giftBoxVariantId: l.giftBox?.variantId ?? null,
          giftNote: l.giftNote ?? null,
          personalisation: l.personalisation ?? null,
          monogramVariantId: l.monogramVariantId ?? null,
        })),
      };
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // ignore (storage may be full/disabled)
    }
  }, [step, maxStepReached, selectedOptionsByProductId, lines]);

  function upsertLine(next: Omit<SelectedLine, 'key'>) {
    setLines(prev => {
      const hasPersonalisation = Boolean(next.personalisation);
      const idx = prev.findIndex(
        l => l.variantId === next.variantId && Boolean(l.personalisation) === hasPersonalisation,
      );
      if (idx === -1) return [...prev, {...next, key: uid()}];
      const copy = [...prev];
      copy[idx] = {...copy[idx], ...next, quantity: next.quantity};
      return copy;
    });
  }

  function addVariant(
    stepId: 1 | 2,
    product: ProductData,
    variant: VariantOption,
    qty: number,
    personalisation?: PersonalisationData | null,
    monogramVariantId?: number | null,
  ) {
    if (!variant.available) return;
    const hasPersonalisation = Boolean(personalisation);
    const existing = lines.find(
      l => l.variantId === variant.id && Boolean(l.personalisation) === hasPersonalisation,
    );
    const groupId = existing?.groupId ?? uid();
    upsertLine({
      stepId,
      productId: product.id,
      productTitle: product.title,
      productHandle: product.handle,
      image: product.image ?? null,
      variantId: variant.id,
      variantTitle: variant.title,
      quantity: (existing?.quantity ?? 0) + qty,
      unitPrice: variant.price,
      groupId,
      giftBox: existing?.giftBox ?? null,
      personalisation: personalisation ?? null,
      monogramVariantId: monogramVariantId ?? null,
    });
  }

  function openProductDetailsModal(product: ProductData, stepId: 1 | 2) {
    (window as any).dataLayerUtils?.send('bridal_view_details_clicked');
    window.dispatchEvent(
      new CustomEvent('product-details-modal:open', {
        detail: {
          productUrl: `/products/${product.handle}?view=details-modal`,
          addToBoxCallback: (payload: {
            variantId: number;
            personalisation: PersonalisationData | null;
            monogramVariantId: number | null;
          }) => {
            const variant = product.variants.find(v => v.id === payload.variantId);
            if (!variant) return;
            addVariant(stepId, product, variant, 1, payload.personalisation, payload.monogramVariantId);
          },
        },
      })
    );
  }

  function removeLine(key: string) {
    setLines(prev => prev.filter(l => l.key !== key));
  }

  function updateQty(key: string, qty: number) {
    setLines(prev =>
      prev.map(l => (l.key === key ? {...l, quantity: Math.max(1, qty)} : l)),
    );
  }

  function updateGiftBox(key: string, giftBox: GiftBoxOption | null, giftNote?: string | null) {
    setLines(prev =>
      prev.map(l =>
        l.key === key
          ? {
              ...l,
              giftBox,
              giftNote: giftNote !== undefined ? giftNote : l.giftNote ?? null,
            }
          : l,
      ),
    );
  }

  function updatePersonalisation(key: string, personalisation: PersonalisationData | null, monogramVariantId: number | null) {
    setLines(prev =>
      prev.map(l =>
        l.key === key
          ? {...l, personalisation, monogramVariantId}
          : l,
      ),
    );
  }

  function openPersonalisationModal(line: SelectedLine, product: ProductData) {
    window.dispatchEvent(
      new CustomEvent('personalisation-bsb:open', {
        detail: {
          existing: line.personalisation ?? null,
          callback: (personalisation: PersonalisationData) => {
            updatePersonalisation(line.key, personalisation, product.monogramVariantId ?? null);
          },
        },
      })
    );
  }

  useEffect(() => {
    setMaxStepReached(prev => (step > prev ? step : prev));
  }, [step]);

  useEffect(() => {
    const stepTitle = step <= 2 ? data.steps[step - 1]?.title : data.step_3_title;
    (window as any).dataLayerUtils?.send('bridal_step_view', {
      step_title: stepTitle || `Step ${step}`,
    });
  }, [step]);

  // Product grid loading overlay: reset per step and hide when all images load (or error).
  useEffect(() => {
    if (step === 3) return;
    gridLoadedIdsRef.current = new Set();
    setGridLoadedCount(0);
  }, [step, currentStep?.id]);

  // Deterministic loader: show for 2 seconds then hide.
  useEffect(() => {
    if (step === 3) {
      setShowGridLoader(false);
      return;
    }
    setShowGridLoader(true);
    const t = window.setTimeout(() => setShowGridLoader(false), 500);
    return () => window.clearTimeout(t);
  }, [step, currentStep?.id]);

  function markGridImageLoaded(productId: number) {
    const seen = gridLoadedIdsRef.current;
    if (seen.has(productId)) return;
    seen.add(productId);
    setGridLoadedCount(prev => prev + 1);
  }

  function goToStep(target: 1 | 2 | 3) {
    // Allow going back freely, and forward only if already reached.
    if (target > maxStepReached) return;
    setStep(target);

    const stepLabel = target <= 2 ? data.steps[target - 1]?.title : data.step_3_title;
    (window as any).dataLayerUtils?.send('bridal_navigation_click', {
      step_label: stepLabel || `Step ${target}`,
    });

    try {
      window.scrollTo({top: 0, behavior: 'smooth'});
    } catch {
      window.scrollTo(0, 0);
    }
  }

  function goToNextStep() {
    setStep(prev => {
      const next = prev === 1 ? 2 : 3;
      return next;
    });
    try {
      window.scrollTo({top: 0, behavior: 'smooth'});
    } catch {
      window.scrollTo(0, 0);  
    }
  }

  function resetBuilderState() {
    setLines([]);
    setSelectedOptionsByProductId({});
    setStep(1);
    setMaxStepReached(1);
    setOpenKitProductId(null);
    setOpenSelectorIndex(null);
    setActiveGiftLine(null);
    setActiveGiftProduct(null);
    setStickyDetailsOpen(false);
    try {
      if (typeof window !== 'undefined' && 'sessionStorage' in window) {
        window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
  }

  function addToWishlist(productId: number, variantId: number, button: HTMLButtonElement) {
    (window as any).WishlistKing?.addWishlistItem(productId, variantId);
    button.classList.toggle('is-added');
  }

  /**
   * Stepper line fill should reach the *center* of each circle.
   * With 3 evenly-spaced circles, centers land at ~16.7%, 50%, 83.3%.
   */
  const progressPct = step === 1 ? 16.6667 : step === 2 ? 50 : 83.3333;
  const subtotalFormatted = formatMoneyFallback(subtotalCents);

  return (
    <div className="bsb">
      <header className="bsb__header">
        <div className="page-width">
        <div className="bsb__title-wrap" style={{ ['--bsb-title-align-desktop' as any]: data.titleAlignDesktop || 'flex-start', ['--bsb-title-align-mobile' as any]: data.titleAlignMobile || 'flex-start', ['--bsb-title-text-align-mobile' as any]: ({ 'flex-start': 'left', 'center': 'center', 'flex-end': 'right' } as Record<string, string>)[data.titleAlignMobile || 'flex-start'] || 'left' }}>
          <h1 className="bsb__title h0">{data.title}</h1>
          {data.description ? <p className="bsb__description">{data.description}</p> : null}
        </div>

        <nav
          className="bsb__stepper"
          aria-label={data.strings?.stepperLabel ?? 'Bridal set builder steps'}
          style={{['--bsb-progress' as any]: `${progressPct}%`}}
        >
          <ol className="bsb__stepper-list">
            <li className={step === 1 ? 'bsb__stepper-item is-current' : step > 1 ? 'bsb__stepper-item is-complete' : 'bsb__stepper-item'}>
              <button type="button" className="bsb__stepper-btn" onClick={() => goToStep(1)}>
                <span className="bsb__stepper-circle" aria-hidden="true">1</span>
                {data.steps[0]?.title ? <span className="bsb__stepper-label">{data.steps[0].title}</span> : null}
              </button>
            </li>
            <li className={step === 2 ? 'bsb__stepper-item is-current' : step > 2 ? 'bsb__stepper-item is-complete' : 'bsb__stepper-item'}>
              <button
                type="button"
                className="bsb__stepper-btn"
                onClick={() => goToStep(2)}
                disabled={maxStepReached < 2}
                aria-disabled={maxStepReached < 2}
              >
                <span className="bsb__stepper-circle" aria-hidden="true">2</span>
                {data.steps[1]?.title ? <span className="bsb__stepper-label">{data.steps[1].title}</span> : null}
              </button>
            </li>
            <li className={step === 3 ? 'bsb__stepper-item is-current' : 'bsb__stepper-item'}>
              <button
                type="button"
                className="bsb__stepper-btn"
                onClick={() => goToStep(3)}
                disabled={maxStepReached < 3}
                aria-disabled={maxStepReached < 3}
              >
                <span className="bsb__stepper-circle" aria-hidden="true">3</span>
                {data.step_3_title ? <span className="bsb__stepper-label">{data.step_3_title}</span> : null}

              </button>
            </li>
          </ol>
        </nav>
        </div>
      </header>

      {step !== 3 && currentStep ? (
        <section className="bsb__step page-width">
          <div className="bsb__grid-wrap" aria-busy={!isGridReady}>
            {!isGridReady ? (
              <div className="bsb__grid-overlay" aria-label={data.strings?.loadingProducts ?? 'Loading products'}>
                <div className="bsb__grid-overlay-inner">
                  <div className="bsb__spinner" aria-hidden="true" />
                </div>
              </div>
            ) : null}

            <div className="bsb__grid" role="list">
            {currentStep.products.map((p) => {
              const v = firstAvailableVariant(p);
              const canQuickAdd = Boolean(v?.id) && Boolean(v?.available);
              const isKitOpen = openKitProductId === p.id;
              const optionNames = p.optionNames && p.optionNames.length ? p.optionNames : [];
              const selectedOptions = selectedOptionsByProductId[p.id] ?? [];
              const selectedVariant = selectedOptions.length ? chooseVariantForSelection(p, selectedOptions) : v;

              const colourOptionIndex = optionNames.findIndex(name =>
                /colour|color/i.test(name || ''),
              );
              const firstNonColourIndex = optionNames.findIndex(name =>
                !/colour|color/i.test(name || ''),
              );

              return (
                <div key={p.id} className="bsb__card" role="listitem">
                  <div className="bsb__card-click">
                    <div className="bsb__card-media" aria-label={p.title}>
                      {p.image ? (
                        <img
                          src={p.image}
                          alt=""
                          loading="eager"
                          decoding="async"
                          onLoad={() => markGridImageLoaded(p.id)}
                          onError={() => markGridImageLoaded(p.id)}
                          ref={(el) => {
                            // If the image is already in cache, onLoad may not fire reliably.
                            if (el && el.complete) {
                              markGridImageLoaded(p.id);
                            }
                          }}
                        />
                      ) : (
                        <div className="bsb__card-media--placeholder" />
                      )}

                      {/* Trigger buttons over image (bottom) */}
                      {!isKitOpen ? (
                        <div className="bsb__kit-actions">
                          {p.url || p.handle ? (
                            <button
                              type="button"
                              className="bsb__view-details button button--secondary"
                              onClick={() => openProductDetailsModal(p, currentStep.id)}
                            >
                              {data.strings?.viewDetails ?? 'View details'}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="bsb__kit-trigger button"
                            disabled={!canQuickAdd}
                            onClick={() => {
                              const fallback = firstAvailableVariant(p);
                              const nextDefaultOptions =
                                selectedOptionsByProductId[p.id] ??
                                (fallback?.options ? [...fallback.options] : []);
                              if (nextDefaultOptions.length) {
                                setSelectedOptionsByProductId(prev => ({...prev, [p.id]: nextDefaultOptions}));
                              }
                              setOpenKitProductId(p.id);
                              setOpenSelectorIndex(firstNonColourIndex === -1 ? null : firstNonColourIndex);
                              (window as any).dataLayerUtils?.send('bridal_quick_add_start', {
                                product_title: p.title,
                              });
                            }}
                          >
                            <span>{data.strings?.quickAdd ?? 'Quick add'}</span>
                            <BagIcon className="bsb__kit-icon" />
                          </button>
                        </div>
                      ) : null}

                      {/* In-image overlay panel (white background) */}
                      {isKitOpen ? (
                        <div className="bsb__kit-panel" role="dialog" aria-label={(data.strings?.chooseOptionsFor ?? 'Choose options for {{ product_title }}').replace('{{ product_title }}', p.title)}>
                          <button
                            type="button"
                            className="bsb__kit-close"
                            aria-label={data.strings?.close ?? 'Close'}
                            onClick={() => {
                              setOpenKitProductId(null);
                              setOpenSelectorIndex(null);
                            }}
                          >
                            ×
                          </button>

                          <div className="bsb__kit-content">
                            {optionNames.length ? (
                              <div className="bsb__kit-selectors" aria-label="Options">
                                {optionNames.map((name, optionIdx) => {
                                  const isColourOption = /colour|color/i.test(name || '');
                                  if (isColourOption) {
                                    // Colour is controlled by the swatches under the card, so skip the dropdown here.
                                    return null;
                                  }

                                  const currentValue = selectedOptions[optionIdx] || '';
                                  const allValues = Array.from(
                                    new Set(
                                      p.variants
                                        .map(vr => (vr.options?.[optionIdx] || '').trim())
                                        .filter(Boolean),
                                    ),
                                  );

                                  const isValueEnabled = (val: string) => {
                                    const nextSelected = [...selectedOptions];
                                    nextSelected[optionIdx] = val;
                                    return Boolean(
                                      p.variants.find(vr => vr.available && variantMatchesOptions(vr, nextSelected)),
                                    );
                                  };

                                  const isOpen = openSelectorIndex === optionIdx;

                                  return (
                                    <div className="bsb__selector" key={`${p.id}-${optionIdx}`}>
                                      <button
                                        type="button"
                                        className="bsb__selector-trigger"
                                        onClick={() => setOpenSelectorIndex(prev => (prev === optionIdx ? null : optionIdx))}
                                        aria-expanded={isOpen}
                                      >
                                        <span className="bsb__selector-name">{name}:</span>
                                        <span className="bsb__selector-value">{currentValue || (data.strings?.select ?? 'Select')}</span>
                                      </button>

                                      {isOpen ? (
                                        <div className="bsb__selector-panel" role="listbox" aria-label={`${name} options`}>
                                          {allValues.map(val => {
                                            const active = val === currentValue;
                                            const enabled = isValueEnabled(val);
                                            return (
                                              <button
                                                key={val}
                                                type="button"
                                                className={active ? 'bsb__selector-option is-active' : 'bsb__selector-option'}
                                                disabled={!enabled}
                                                onClick={() => {
                                                  const nextSelected = [...selectedOptions];
                                                  nextSelected[optionIdx] = val;
                                                  setSelectedOptionsByProductId(prev => ({...prev, [p.id]: nextSelected}));
                                                  (window as any).dataLayerUtils?.send('bridal_quick_add_size_select', {
                                                    size_value: val,
                                                  });
                                                }}
                                              >
                                                {val}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}

                            <button
                              type="button"
                              className="button bsb__kit-add"
                              disabled={!selectedVariant?.available}
                              onClick={() => {
                                if (!selectedVariant) return;
                                addVariant(currentStep.id, p, selectedVariant, 1);
                                (window as any).dataLayerUtils?.send('bridal_quick_add_complete', {
                                  product_title: p.title,
                                });
                                setOpenKitProductId(null);
                              }}
                            >
                              <span>{data.strings?.quickAdd ?? 'Quick add'}</span>
                              <BagIcon className="bsb__kit-icon" />
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <div className="bsb__card-info">
                      <div className="card__information-top">
                        <h3 className="bsb__card-title">{p.title}</h3>
                        <button
                          type="button"
                          aria-label={data.strings?.addToWishlist ?? 'Add to wishlist'}
                          className="card__wishlist-add list-menu__item link focus-inset"
                          data-product-handle={p.handle}
                          data-product-id={p.id}
                          data-variant-id={selectedVariant?.id ?? v?.id}
                          onClick={event => {
                            const wishlistVariantId = selectedVariant?.id ?? v?.id;
                            if (!wishlistVariantId) return;
                            addToWishlist(p.id, wishlistVariantId, event.currentTarget);
                          }}
                        >
                          <svg
                            style={{pointerEvents: 'none'}}
                            className="icon"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M11.4509 5.44994L11.0973 5.8035L11.2929 5.99909C11.6834 6.38961 12.3166 6.38961 12.7071 5.99909L12.9027 5.80356C14.6407 4.06557 17.4585 4.06556 19.1965 5.80356C20.9345 7.54155 20.9345 10.3594 19.1965 12.0974L12 19.2939L4.8035 12.0973C3.0655 10.3593 3.0655 7.54149 4.8035 5.8035C6.54149 4.0655 9.35934 4.0655 11.0973 5.8035L11.4509 5.44994Z"
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                      <div
                        className="bsb__card-price"
                        dangerouslySetInnerHTML={{__html: moneyHtml(v?.price?.formatted, v?.price?.cents ?? 0)}}
                      />
                     
                    </div>
                  </div>
                  <div className="bsb__mobile-buttons">
                      {p.url || p.handle ? (
                        <button
                          type="button"
                          className="bsb__view-details button button--secondary"
                          onClick={() => openProductDetailsModal(p, currentStep.id)}
                        >
                          {data.strings?.viewDetails ?? 'View details'}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="bsb__kit-trigger button"
                        disabled={!canQuickAdd}
                        onClick={() => {
                          const fallback = firstAvailableVariant(p);
                          const nextDefaultOptions =
                            selectedOptionsByProductId[p.id] ??
                            (fallback?.options ? [...fallback.options] : []);
                          if (nextDefaultOptions.length) {
                            setSelectedOptionsByProductId(prev => ({...prev, [p.id]: nextDefaultOptions}));
                          }
                          setOpenKitProductId(p.id);
                          setOpenSelectorIndex(firstNonColourIndex === -1 ? null : firstNonColourIndex);
                          (window as any).dataLayerUtils?.send('bridal_quick_add_start', {
                            product_title: p.title,
                          });
                        }}
                      >
                       {data.strings?.quickAdd ?? 'Quick add'} <BagIcon className="bsb__kit-icon" />
                  </button>
                </div>
            </div>
              );
            })}
            </div>
          </div>
        </section>
      ) : null}

      {step === 3 ? (
        <section className="bsb__review page-width">
          <div className="bsb__review-head">
            <h2 className="h1">{data.step_3_title ?? data.strings?.reviewSelection ?? 'Review selection'}</h2>
            <p>{data.strings?.reviewDescription ?? 'Adjust quantities, remove items, and add gift boxes per item.'}</p>
          </div>

          {lines.length === 0 ? (
            <div className="bsb__empty">
              <p>{data.strings?.emptyState ?? 'No items selected yet.'}</p>
              <button className="button" type="button" onClick={() => setStep(1)}>
                {data.strings?.goToStep1 ?? 'Go to Step 1'}
              </button>
            </div>
          ) : (
            <div className="bsb__review-list">
              {lines.map((l) => (
                <div key={l.key} className="bsb__review-item">
                  <div className="bsb__review-media">
                    {l.image ? <img src={l.image} alt="" loading="lazy" /> : <div className="bsb__card-media--placeholder" />}
                  </div>

                  <div className="bsb__review-main">
                    <div className="bsb__review-title">
                      {l.productTitle}
                      <span
                      className="bsb__review-price desktop-only"
                        dangerouslySetInnerHTML={{
                          __html: moneyHtml(l.unitPrice.formatted, l.unitPrice.cents),
                        }}
                      />
                      </div>
                    <div className="bsb__review-meta">
                      <span>{l.variantTitle}</span>
                    
                    </div>
                    <div className="bsb__review-price mobile-only">
                      <span dangerouslySetInnerHTML={{
                        __html: moneyHtml(l.unitPrice.formatted, l.unitPrice.cents),
                      }} />
                    </div>
                    <div className="bsb__review-controls">
                      <div className="bsb__qty">
                        <button type="button" className="bsb__qty-btn" onClick={() => updateQty(l.key, l.quantity - 1)}>
                          <span dangerouslySetInnerHTML={{  
                            __html: `<svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect x="17" y="10" width="1" height="13" rx="0.5" transform="rotate(90 17 10)" fill="#475751"/>
</svg>

`,
                          }} />
                        </button>
                        <input
                          className="bsb__qty-input"
                          type="number"
                          min={1}
                          value={l.quantity}
                          onChange={(e) => updateQty(l.key, Number(e.target.value || 1))}
                        />
                        <button type="button" className="bsb__qty-btn" onClick={() => updateQty(l.key, l.quantity + 1)}>
                          <span dangerouslySetInnerHTML={{
                            __html: `<svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M16.9531 10.0625C17.2552 10.0625 17.5 10.3073 17.5 10.6094C17.5 10.9114 17.2552 11.1562 16.9531 11.1562L4.04687 11.1562C3.74484 11.1562 3.5 10.9114 3.5 10.6094C3.5 10.3073 3.74484 10.0625 4.04688 10.0625L16.9531 10.0625Z" fill="#475751"/>
<rect x="10.9375" y="17.5" width="1.09375" height="14" rx="0.546875" transform="rotate(-180 10.9375 17.5)" fill="#475751"/>
</svg>

`,
                          }} />
                        </button>
                      </div>

                      <button type="button" className="link bsb__remove" onClick={() => { (window as any).dataLayerUtils?.send('bridal_remove_product'); removeLine(l.key); }}>
                        {data.strings?.remove ?? 'Remove'}
                      </button>
                    </div>

                    <GiftBoxControls
                      line={l}
                      product={data.steps[l.stepId === 1 ? 0 : 1].products.find(p => p.id === l.productId) ?? null}
                      onChange={updateGiftBox}
                      onOpenDrawer={(line, product) => {
                        setActiveGiftLine(line);
                        setActiveGiftProduct(product);
                      }}
                      strings={data.strings}
                    />
                    <PersonalisationControls
                      line={l}
                      product={data.steps[l.stepId === 1 ? 0 : 1].products.find(p => p.id === l.productId) ?? null}
                      monogramPriceCents={data.monogramPriceCents ?? 0}
                      onEdit={openPersonalisationModal}
                      onRemove={(key: string) => updatePersonalisation(key, null, null)}
                      strings={data.strings}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="bsb__review-footer">
            <div className="bsb__review-footer-inner">
              <div className="bsb__review-items">
                <span>{data.strings?.items ?? 'Items'}:</span>
                <span>
                  {lines.reduce((sum, l) => {
                    let count = l.quantity;
                    if (l.giftBox) count += l.quantity;
                    if (l.personalisation) count += l.quantity;
                    return sum + count;
                  }, 0)}
                </span>
              </div>
              <div className="bsb__review-subtotal">
                
                <span>{data.strings?.subtotal ?? 'Subtotal'}:</span>
                <strong
                  dangerouslySetInnerHTML={{
                    __html: moneyHtml(undefined, subtotalCents),
                  }}
                />
              </div>
              <div className="bsb__review-ctas">
                  <button
                    type="button"
                    className="button"
                    disabled={lines.length === 0}
                    onClick={async () => {
                      (window as any).dataLayerUtils?.send('bridal_journey_completed');
                      try {
                        await addToCartAndCheckout(lines);
                        resetBuilderState();
                      } catch (e) {
                        // eslint-disable-next-line no-alert
                        alert(data.strings?.cartError ?? 'Sorry — could not add to cart. Please try again.');
                      }
                    }}
                  >
                  {data.strings?.addToCart ?? 'Add to cart'} <span dangerouslySetInnerHTML={{
                    __html: `<svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.6047 3.38678L9.84011 0.622147C9.69778 0.479821 9.69778 0.249065 9.84011 0.10674C9.98243 -0.0355867 10.2132 -0.0355862 10.3555 0.10674L13.6464 3.39767C13.8417 3.59293 13.8417 3.90952 13.6464 4.10478L10.3555 7.39571C10.2132 7.53803 9.98243 7.53803 9.84011 7.39571C9.69778 7.25338 9.69778 7.02263 9.84011 6.8803L12.6047 4.11567L0.364449 4.11567C0.163169 4.11567 3.23544e-07 3.9525 3.4114e-07 3.75122C3.58736e-07 3.54994 0.163169 3.38677 0.364449 3.38677L12.6047 3.38678Z" fill="#F3F3F3"/>
</svg>`,
                  }} />
                </button>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {lines.length > 0 && step !== 3 ? (
        <footer
          className={stickyDetailsOpen ? 'bsb__sticky bsb__sticky--details-open' : 'bsb__sticky'}
          aria-label={data.strings?.selectionSummary ?? 'Selection summary'}
        >
          <div className="bsb__sticky-inner page-width">
            <div className="bsb__sticky-left">

              <div className="bsb__sticky-carousel" aria-label={data.strings?.selectedItems ?? 'Selected items'}>
                {lines.length > 2 ? (
                  <button
                    type="button"
                    className="bsb__sticky-arrow bsb__sticky-arrow--prev"
                    aria-label={data.strings?.scrollLeft ?? 'Scroll selection left'}
                    onClick={() => {
                      const el = stickyListRef.current;
                      if (!el) return;
                      el.scrollBy({left: -Math.max(240, el.clientWidth * 0.8), behavior: 'smooth'});
                    }}
                  >
                    ‹
                  </button>
                ) : (
                  <span aria-hidden="true" />
                )}

                <div className="bsb__sticky-items" role="list" ref={stickyListRef}>
                  {lines.map((line) => (
                    <div className="bsb__sticky-item" key={line.key} role="listitem" title={line.productTitle}>
                      <button
                        type="button"
                        className="bsb__sticky-remove"
                        aria-label={`Remove ${line.productTitle}`}
                        onClick={() => removeLine(line.key)}
                      >
                        <span dangerouslySetInnerHTML={{
                          __html: `<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M6.64645 7.35355L0 0.707107L0.707107 0L7.35355 6.64645L14 0L14.7071 0.707107L8.06066 7.35355L14.7071 14L14 14.7071L7.35355 8.06066L0.707107 14.7071L0 14L6.64645 7.35355Z" fill="#222222"/>
</svg>`,
                        }} />
                      </button>
                      <div className="bsb__sticky-thumb">
                        {line.image ? <img src={line.image} alt="" loading="lazy" /> : null}
                      </div>
                      <div className="bsb__sticky-text">
                        <div className="bsb__sticky-name">{line.productTitle}</div>
                        <div className="bsb__sticky-meta">
                          <span>{line.variantTitle}</span>
                          {line.personalisation && (
                            <>
                              <span className="bsb__dot">·</span>
                              <span className="bsb__sticky-meta-personalised">
                                &ldquo;{line.personalisation.initials}&rdquo;
                                {line.personalisation.font ? ` · ${line.personalisation.font}` : ''}
                                {line.personalisation.color ? ` · ${line.personalisation.color}` : ''}
                              </span>
                            </>
                          )}
                          <span className="bsb__dot">·</span>
                          <span>{data.strings?.items ?? 'Items'}: {line.quantity}</span>
                        </div>
                        <div className="bsb__sticky-price">
                          {line.personalisation && data.monogramPriceCents ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: moneyHtml(undefined, line.unitPrice.cents + data.monogramPriceCents),
                              }}
                            />
                          ) : (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: moneyHtml(line.unitPrice.formatted, line.unitPrice.cents),
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {lines.length > 2 ? (
                  <button
                    type="button"
                    className="bsb__sticky-arrow bsb__sticky-arrow--next"
                    aria-label={data.strings?.scrollRight ?? 'Scroll selection right'}
                    onClick={() => {
                      const el = stickyListRef.current;
                      if (!el) return;
                      el.scrollBy({left: Math.max(240, el.clientWidth * 0.8), behavior: 'smooth'});
                    }}
                  >
                    ›
                  </button>
                ) : (
                  <span aria-hidden="true" />
                )}
              </div>
            </div>

            <div className="bsb__sticky-summary">
              <div className="bsb__sticky-summary-top">
                <div className="bsb__sticky-subtotal">
                  <span>{data.strings?.boxSubtotal ?? 'Box subtotal'}:</span>
                  <strong
                    dangerouslySetInnerHTML={{
                      __html: moneyHtml(undefined, subtotalCents),
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="bsb__sticky-toggle"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setStickyDetailsOpen(prev => !prev);
                  }}
                >
                  {stickyDetailsOpen ? (data.strings?.hideDetails ?? 'Hide details') : (data.strings?.showDetails ?? 'Show details')} <span dangerouslySetInnerHTML={{
                    __html: `<svg width="9" height="6" viewBox="0 0 9 6" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8.5 4.70703L4.5 0.707031L0.5 4.70703" stroke="#222222" stroke-linecap="round"/>
</svg>
`,
                  }} />
                </button>
              </div>
              <button
                type="button"
                className="button bsb__sticky-next"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  (window as any).dataLayerUtils?.send('bridal_next_step_click');
                  goToNextStep();
                }}
              >
                {step === 1 ? (data.strings?.nextStep ?? 'Next step') : (data.strings?.reviewSelection ?? 'Review selection')}
                <span
                  dangerouslySetInnerHTML={{
                    __html: `<svg width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M12.6047 3.38678L9.84011 0.622147C9.69778 0.479821 9.69778 0.249065 9.84011 0.10674C9.98243 -0.0355867 10.2132 -0.0355862 10.3555 0.10674L13.6464 3.39767C13.8417 3.59293 13.8417 3.90952 13.6464 4.10478L10.3555 7.39571C10.2132 7.53803 9.98243 7.53803 9.84011 7.39571C9.69778 7.25338 9.69778 7.02263 9.84011 6.8803L12.6047 4.11567L0.364449 4.11567C0.163169 4.11567 3.23544e-07 3.9525 3.4114e-07 3.75122C3.58736e-07 3.54994 0.163169 3.38677 0.364449 3.38677L12.6047 3.38678Z" fill="#F3F3F3"/>
</svg>`,
                  }}
                />
              </button>
            </div>
          </div>
        </footer>
      ) : null}

      {activeGiftLine && activeGiftProduct ? (
        <GiftBoxDrawer
          line={activeGiftLine}
          product={activeGiftProduct}
          onConfirm={(key, giftBox, giftNote) => {
            updateGiftBox(key, giftBox, giftNote);
            setActiveGiftLine(null);
            setActiveGiftProduct(null);
          }}
          onClose={() => {
            setActiveGiftLine(null);
            setActiveGiftProduct(null);
          }}
          strings={data.strings}
        />
      ) : null}

    </div>
  );
}

function GiftBoxControls({
  line,
  product,
  onChange,
  onOpenDrawer,
  strings,
}: {
  line: SelectedLine;
  product: ProductData | null;
  onChange: (key: string, giftBox: GiftBoxOption | null, giftNote?: string | null) => void;
  onOpenDrawer?: (line: SelectedLine, product: ProductData) => void;
  strings?: BuilderStrings;
}) {
  if (!product) return null;
  const giftBoxes = product.giftBoxes ?? [];
  if (!giftBoxes.length) return null;

  const selectedGiftBox = line.giftBox ?? null;
  const selectedPriceHtml = selectedGiftBox
    ? moneyHtml(selectedGiftBox.price.formatted, selectedGiftBox.price.cents)
    : null;

  return (
    <div className="bsb__giftbox">
      {selectedGiftBox ? (
        <div className="bsb__giftbox-summary">
          <div className="bsb__giftbox-summary-main">
            <span className="bsb__giftbox-summary-title">
              {selectedGiftBox.title}
              <span
                className="bsb__giftbox-summary-price"
                dangerouslySetInnerHTML={{
                  __html: selectedPriceHtml || '',
                }}
              />
            </span>
            {line.giftNote ? (
              <span className="bsb__giftbox-summary-details">
                <span>{strings?.noteLabel ?? 'Note'}: &ldquo;{line.giftNote}&rdquo;</span>
              </span>
            ) : null}
          </div>
          <div className="bsb__giftbox-summary-actions">
            <button
              type="button"
              className="bsb__giftbox-summary-edit"
              onClick={() => {
                (window as any).dataLayerUtils?.send('bridal_edit_gift_message');
                if (onOpenDrawer) {
                  onOpenDrawer(line, product);
                }
              }}
            >
              {strings?.edit ?? 'Edit'}
            </button>
            <button
              type="button"
              className="bsb__giftbox-summary-remove"
              onClick={() => {
                (window as any).dataLayerUtils?.send('bridal_remove_gift_box');
                onChange(line.key, null, null);
              }}
            >
              {strings?.remove ?? 'Remove'}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="bsb__giftbox-toggle"
          onClick={() => {
            (window as any).dataLayerUtils?.send('bridal_gift_box_start');
            if (onOpenDrawer) {
              onOpenDrawer(line, product);
            }
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: `<svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M2.59264 0.25C2.00706 0.25 1.55045 0.482444 1.26742 0.815808C0.984399 1.14917 0.870364 1.56288 0.870364 1.9375C0.870364 2.48098 1.11749 2.91719 1.43117 3.22794C1.45372 3.25026 1.47755 3.27117 1.50073 3.29246H0.582576C0.494458 3.29272 0.410023 3.32784 0.347711 3.39014C0.285398 3.45245 0.250271 3.53688 0.25 3.625V6.99999C0.250269 7.08811 0.285395 7.17254 0.347708 7.23485C0.410021 7.29715 0.494457 7.33227 0.582576 7.33253H0.870364V13.4175C0.870637 13.5056 0.905765 13.59 0.968078 13.6523C1.03039 13.7146 1.11482 13.7497 1.20294 13.75H12.9559C12.9999 13.7505 13.0436 13.7423 13.0845 13.7258C13.1253 13.7094 13.1625 13.685 13.1939 13.654C13.2253 13.6231 13.2502 13.5863 13.2673 13.5457C13.2844 13.5051 13.2932 13.4615 13.2934 13.4175V7.33253H13.5762C13.6644 7.33227 13.7488 7.29715 13.8111 7.23485C13.8734 7.17254 13.9085 7.08811 13.9088 6.99999V3.625C13.909 3.54257 13.8787 3.46298 13.8237 3.40157C13.7687 3.34017 13.693 3.3013 13.611 3.29246C13.5994 3.29186 13.5878 3.29186 13.5762 3.29246H12.6581C12.6811 3.27117 12.7049 3.25026 12.7275 3.22794C13.0413 2.91719 13.2934 2.48098 13.2934 1.9375C13.2934 1.56288 13.1743 1.14917 12.8914 0.815808C12.6084 0.482443 12.1517 0.25 11.5662 0.25C10.668 0.25 9.67188 0.791251 8.78167 1.44118C8.08984 1.94638 7.47964 2.51683 7.0794 3.03437C6.67901 2.51683 6.06881 1.94638 5.37698 1.44118C4.48693 0.791249 3.49079 0.25 2.59264 0.25ZM2.59264 0.920036C3.19633 0.920036 4.1534 1.37501 4.98484 1.98217C5.55597 2.39909 6.07469 2.88631 6.42426 3.29246H2.68699C2.40333 3.13537 2.113 2.95967 1.90272 2.75147C1.67512 2.52607 1.53552 2.28882 1.53552 1.9375C1.53552 1.70331 1.61255 1.43746 1.77375 1.24761C1.93464 1.05776 2.17081 0.920036 2.59264 0.920036ZM11.5662 0.920036C11.988 0.920036 12.2239 1.05776 12.3851 1.24761C12.5463 1.43746 12.6233 1.70331 12.6233 1.9375C12.6233 2.28882 12.4886 2.52607 12.261 2.75147C12.0507 2.95967 11.7604 3.13538 11.4767 3.29246H7.73455C8.08396 2.88631 8.60284 2.39909 9.17381 1.98217C10.0054 1.37501 10.9625 0.920036 11.5662 0.920036ZM0.919917 3.95753H2.56279C2.58423 3.95961 2.60582 3.95961 2.62727 3.95753H6.74683V6.66746H0.919917V3.95753ZM7.41198 3.95753H11.4718C11.5256 3.97114 11.5818 3.97114 11.6356 3.95753H13.2437V6.66746H7.41198V3.95753ZM1.53552 7.33253H6.74683V13.0849H1.53552V7.33253ZM7.41198 7.33253H12.6233V13.0849H7.41198V7.33253Z" fill="#475751" stroke="#475751" stroke-width="0.5"/>
</svg>`,
            }}
          />
          <span className="bsb__giftbox-toggle-text">{strings?.addGiftBox ?? 'Add gift box'}</span>
        </button>
      )}
    </div>
  );
}

function PersonalisationControls({
  line,
  product,
  monogramPriceCents,
  onEdit,
  onRemove,
  strings,
}: {
  line: SelectedLine;
  product: ProductData | null;
  monogramPriceCents: number;
  onEdit: (line: SelectedLine, product: ProductData) => void;
  onRemove: (key: string) => void;
  strings?: BuilderStrings;
}) {
  if (!product?.supportsPersonalisation) return null;

  const p = line.personalisation;
  const priceHtml = monogramPriceCents ? formatMoneyFallback(monogramPriceCents) : null;

  return (
    <div className="bsb__personalisation">
      {p ? (
        <div className="bsb__personalisation-summary">
          <div className="bsb__personalisation-summary-main">
            <span className="bsb__personalisation-summary-title">
              {strings?.personalisationAdded ?? 'Monogram Personalisation added'}
              {priceHtml ? <span className="bsb__personalisation-summary-price">{priceHtml}</span> : null}
            </span>
            <span className="bsb__personalisation-summary-details">
              <span>{strings?.personalisationInitialsLabel ?? 'Options'}: &ldquo;{p.initials}&rdquo;</span>
              <span>{strings?.personalisationFontLabel ?? 'Font'}: {p.font}</span>
              <span>{strings?.personalisationColourLabel ?? 'Colour'}: {p.color}</span>
            </span>
          </div>
          <div className="bsb__personalisation-summary-actions">
            <button
              type="button"
              className="bsb__personalisation-summary-edit"
              onClick={() => onEdit(line, product)}
            >
              {strings?.edit ?? 'Edit'}
            </button>
            <button
              type="button"
              className="bsb__personalisation-summary-remove"
              onClick={() => onRemove(line.key)}
            >
              {strings?.remove ?? 'Remove'}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="bsb__personalisation-toggle"
          onClick={() => onEdit(line, product)}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="#475751"/></svg>`,
            }}
          />
          <span className="bsb__personalisation-toggle-text">{strings?.addPersonalisation ?? 'Add personalisation'}</span>
        </button>
      )}
    </div>
  );
}

function GiftBoxDrawer({
  line,
  product,
  onConfirm,
  onClose,
  strings,
}: {
  line: SelectedLine;
  product: ProductData;
  onConfirm: (key: string, giftBox: GiftBoxOption | null, giftNote: string | null) => void;
  onClose: () => void;
  strings?: BuilderStrings;
}) {
  const giftBoxes = product.giftBoxes ?? [];
  if (!giftBoxes.length) return null;

  const [selectedId, setSelectedId] = useState<number>(
    line.giftBox?.variantId ?? giftBoxes[0]?.variantId ?? giftBoxes[0]?.variantId,
  );

  const [note, setNote] = useState<string>(line.giftNote ?? '');

  const selectedGiftBox = giftBoxes.find(g => g.variantId === selectedId) ?? giftBoxes[0];

  return (
    <div className="bsb__giftbox-drawer open" role="dialog" aria-modal="true" aria-label={strings?.giftingOptionsTitle ?? 'Gift box options'}>
      <div className="bsb__modal-overlay" onClick={onClose} />
      <div className="bsb__giftbox-drawer-inner">
        <button
          type="button"
          className="bsb__modal-close"
          aria-label={strings?.close ?? 'Close'}
          onClick={onClose}
        >
          <span dangerouslySetInnerHTML={{
            __html: `<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M6.64645 7.35355L0 0.707107L0.707107 0L7.35355 6.64645L14 0L14.7071 0.707107L8.06066 7.35355L14.7071 14L14 14.7071L7.35355 8.06066L0.707107 14.7071L0 14L6.64645 7.35355Z" fill="#222222"/>
</svg>`,
          }} />
        </button>
        <h3 className="bsb__giftbox-drawer-title">{strings?.giftingOptionsTitle ?? 'Gifting options'}</h3>
        <div className="bsb__giftbox-drawer-content">
          <h4 className="bsb__giftbox-drawer-subtitle">{strings?.addGiftBoxTitle ?? 'Add a gift box'}</h4>
          <p className="bsb__giftbox-drawer-description">
            {strings?.giftBoxDescription ?? "When you've found the perfect gift, add a luxury gift box for that finishing touch. Any boxes purchased will be sent in your order ready for you to open and fill with your pick of Chelsea Peers pyjamas."}
          </p>
        </div>
        <div className="bsb__giftbox-drawer-checkboxes">
          {giftBoxes.map(g => (
            <div key={g.variantId} className="bsb__giftbox-drawer-checkbox">
              <input
                type="radio"
                className="bsb__giftbox-drawer-radio"
                id={`bsb-giftbox-${line.key}-${g.variantId}`}
                name={`gift-box-${line.key}`}
                value={g.variantId}
                checked={selectedId === g.variantId}
                onChange={() => setSelectedId(g.variantId)}
              />
              <label
                htmlFor={`bsb-giftbox-${line.key}-${g.variantId}`}
                className="bsb__giftbox-drawer-checkbox-label"
              >
                <span className="bsb__giftbox-drawer-checkbox-title">{(strings?.addWithTitle ?? 'Add {{ title }}').replace('{{ title }}', g.title)}</span>
                <span
                  className="bsb__giftbox-drawer-checkbox-price"
                  dangerouslySetInnerHTML={{
                    __html: moneyHtml(g.price.formatted, g.price.cents),
                  }}
                />
              </label>
            </div>
          ))}
        </div>
        <div className="bsb__giftbox-drawer-note">
          <p className="bsb__giftbox-drawer-note-text">{strings?.noteLabel ?? 'Note'}</p>
          <textarea
            className="bsb__giftbox-drawer-note-textarea"
            placeholder={strings?.giftNotePlaceholder ?? 'Add a note for your gift box'}
            maxLength={100}
            value={note}
            onChange={e => {
              // mirror existing gifting flows: strip non-ASCII / emojis
              const clean = e.target.value.replace(/[^\u0000-\u007f]/g, '');
              setNote(clean);
            }}
          />
          <span className="bsb__giftbox-drawer-note-disclaimer">{strings?.emojisNotSupported ?? 'Emojis are not supported.'}</span>
        </div>
        <div className="bsb__giftbox-drawer-confirm">
          <button
            type="button"
            className="button bsb__giftbox-drawer-confirm-button"
            onClick={() => {
              (window as any).dataLayerUtils?.send('bridal_gift_box_complete');
              onConfirm(line.key, selectedGiftBox ?? null, note || null);
            }}
          >
            {strings?.confirmChoices ?? 'Confirm choices'}
          </button>
        </div>
      </div>
    </div>
  );
}
